import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useNavigationType } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { listingsAPI, locationsAPI, shippingAddressesAPI, Listing, State, City } from '../api';
import ListingCard from '../components/ListingCard';
import HomeLocationModal from '../components/HomeLocationModal';
import {
  loadHomeDeliveryLocation,
  saveHomeDeliveryLocation,
  saveDeliveryLocationFromModal,
  hasUserDeliveryLocation,
} from '../utils/homeLocationStorage';
import { getCurrentLocation, findLocationIds, geocodeLocation } from '../utils/geolocation';
import { isDefaultAddress, parseStreetFromAddress, parseCityFromAddress, parseStateFromAddress } from '../utils/addressUtils';
import { LISTING_CATEGORY_KEYS, formatListingCategoryLabel } from '../constants/listingCategories';
import {
  algoliaHitToListing,
  matchesItemType,
  searchAlgoliaListings,
} from '../services/algoliaSearch';
import { filterListingsByDeliveryRange } from '../utils/listingDistanceFilter';
import {
  consumeHomeBrowseState,
  homeCoordsKey,
  homeFiltersKey,
  readHomeListingsCache,
  saveHomeBrowseState,
  saveHomeListingsCache,
} from '../utils/homeScrollRestoration';

const PAGE_SIZE = 24;

const EMPTY_FILTERS = {
  state_id: '',
  city_id: '',
  category: '',
  search: '',
  item_type: '',
};

function coordsNearlyEqual(
  a: { lat: number; lng: number } | null | undefined,
  b: { lat: number; lng: number } | null | undefined,
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return Math.abs(a.lat - b.lat) < 1e-5 && Math.abs(a.lng - b.lng) < 1e-5;
}

function formatListingsLoadError(error: unknown): string {
  const e = error as any;
  const noResponse = !e?.response && e?.request;
  const st = e?.response?.status;
  const isProxyDown = st === 502 || st === 504;
  const isNetwork =
    isProxyDown ||
    e?.code === 'ECONNREFUSED' ||
    e?.code === 'ERR_NETWORK' ||
    e?.message === 'Network Error' ||
    noResponse;
  if (isNetwork) {
    return 'Cannot reach the API. From the repo root run npm run dev — the web app waits for the API (default port 5001). If this persists, check [api] logs in that terminal for database or port errors.';
  }
  const srvErr = e?.response?.data?.error;
  const details = e?.response?.data?.details;
  const piece = [srvErr, details].filter(Boolean).join(' — ');
  if (st != null) {
    return `Couldn't load listings (HTTP ${st}${piece ? `: ${piece}` : ''}).`;
  }
  return `Failed to load listings: ${e?.message || 'Please try again.'}`;
}

const Home: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const restoreBrowseRef = useRef(
    navigationType === 'POP' ? consumeHomeBrowseState() : null,
  );
  const scrollRestoreDoneRef = useRef(false);

  const initialStored =
    typeof window !== 'undefined' ? loadHomeDeliveryLocation() : null;
  const initialCoords =
    initialStored?.lat != null && initialStored?.lng != null
      ? { lat: initialStored.lat, lng: initialStored.lng }
      : null;
  // Delivery address must not seed city/state filters — that narrows the feed to one
  // city and disables delivery-radius filtering (same as iOS Home).
  const initialFilters = { ...EMPTY_FILTERS };
  const initialCache =
    typeof window !== 'undefined'
      ? readHomeListingsCache(homeFiltersKey(initialFilters), homeCoordsKey(initialCoords))
      : null;
  const skipNextFetchRef = useRef(!!initialCache);

  const [listings, setListings] = useState<Listing[]>(() => initialCache?.listings || []);
  const [offset, setOffset] = useState(() => initialCache?.offset || 0);
  const [hasMore, setHasMore] = useState(() => initialCache?.hasMore ?? true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(() => !initialCache);
  const [locationLoading, setLocationLoading] = useState(false);
  const [userLocation, setUserLocation] = useState<{ city?: string; state?: string; streetAddress?: string; streetNumber?: string; streetName?: string } | null>(() => {
    if (!initialStored) return null;
    const label = (initialStored.label || '').trim();
    const streetLine = label ? label.split(',')[0]?.trim() || '' : '';
    if (initialStored.city || initialStored.state || streetLine || initialStored.zip) {
      return {
        city: initialStored.city,
        state: initialStored.state,
        streetAddress: streetLine || initialStored.zip || undefined,
      };
    }
    return null;
  });
  const [filters, setFilters] = useState({ ...initialFilters });
  const [zipCode, setZipCode] = useState(() => initialStored?.zip || '');
  const [zipCodeLoading, setZipCodeLoading] = useState(false);
  const [zipCodeError, setZipCodeError] = useState('');
  const [userCoordinates, setUserCoordinates] = useState<{ lat: number; lng: number } | null>(
    () => initialCoords,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  /** Listing id -> distance in miles (when user has coordinates and distance filtering ran) */
  const [listingDistances, setListingDistances] = useState<Record<number, number>>(
    () => initialCache?.listingDistances || {},
  );
  const listingsLoadGeneration = useRef(0);
  /** In-range listings left over after filling PAGE_SIZE (so Load more doesn't skip them). */
  const pendingInRangeRef = useRef<Listing[]>(initialCache?.pendingInRange?.slice() || []);
  const pendingDistancesRef = useRef<Record<number, number>>(
    initialCache?.pendingDistances ? { ...initialCache.pendingDistances } : {},
  );
  /** Keep API offset in a ref so Load more never uses a stale closure value. */
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const algoliaPageRef = useRef(0);
  /** Wait for default-address bootstrap before first fetch (avoids show-all then filter flash). */
  const [locationBootstrap, setLocationBootstrap] = useState<'pending' | 'done'>('pending');

  const setCoordinatesIfChanged = useCallback((next: { lat: number; lng: number } | null) => {
    setUserCoordinates((prev) => (coordsNearlyEqual(prev, next) ? prev : next));
  }, []);

  // Get the selected city and state names for display
  const selectedCity = cities.find(c => c.id.toString() === filters.city_id);
  const selectedState = states.find(s => s.id.toString() === filters.state_id);

  useEffect(() => {
    locationsAPI.getStates().then((statesData) => {
      setStates(Array.isArray(statesData) ? statesData : (statesData?.data || []));
    }).catch(() => setStates([]));
  }, []);

  // Restore delivery location from storage, then seed from default address only if none saved.
  const applyStoredDeliveryLocation = useCallback(async (isCancelled: () => boolean) => {
    const stored = loadHomeDeliveryLocation();
    if (!stored) return;

    const label = (stored.label || '').trim();
    const streetLine = label ? label.split(',')[0]?.trim() || '' : '';
    if (stored.city || stored.state || streetLine || stored.zip) {
      setUserLocation({
        city: stored.city,
        state: stored.state,
        streetAddress: streetLine || stored.zip || undefined,
      });
    }
    if (stored.zip) setZipCode(stored.zip);
    if (stored.lat != null && stored.lng != null) {
      setCoordinatesIfChanged({ lat: stored.lat, lng: stored.lng });
    }

    // Heal missing IDs in the background — do not block listing load.
    if ((stored.state_id == null || stored.city_id == null) && stored.city && stored.state) {
      findLocationIds(stored.city, stored.state)
        .then((ids) => {
          if (isCancelled()) return;
          if (ids.stateId != null || ids.cityId != null) {
            saveHomeDeliveryLocation({
              city_id: ids.cityId ?? null,
              state_id: ids.stateId ?? null,
              city: stored.city,
              state: stored.state,
              lat: stored.lat,
              lng: stored.lng,
              label: stored.label,
              zip: stored.zip,
            });
          }
        })
        .catch(() => {});
    }
  }, [setCoordinatesIfChanged]);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    const geocodeDeliverTo = (fullAddress: string) => {
      const address = (fullAddress || '').trim();
      if (!address) return;
      locationsAPI.geocode(address).then((coords) => {
        if (cancelled) return;
        const c = coords as { lat: number | null; lng?: number; lon?: number } | null;
        const lat = c?.lat;
        const lng = c && ('lng' in c ? c.lng : c.lon);
        if (lat != null && lng != null) {
          setCoordinatesIfChanged({ lat, lng });
          saveHomeDeliveryLocation({ lat, lng, fullAddress: address });
        }
      }).catch(() => {});
    };

    (async () => {
      await applyStoredDeliveryLocation(isCancelled);
      if (cancelled) return;

      // Prefer having coords before first fetch so we don't flash "all" then filter down.
      const stored = loadHomeDeliveryLocation();
      if (stored && (stored.lat == null || stored.lng == null)) {
        const healAddress =
          (stored.label || '').trim() ||
          [stored.city, stored.state].filter(Boolean).join(', ') ||
          '';
        if (healAddress && /[a-zA-Z]/.test(healAddress) && !/^\d{5}$/.test(healAddress)) {
          try {
            await Promise.race([
              locationsAPI.geocode(healAddress).then((coords) => {
                if (cancelled) return;
                const c = coords as { lat: number | null; lng?: number; lon?: number } | null;
                const lat = c?.lat;
                const lng = c && ('lng' in c ? c.lng : c.lon);
                if (lat != null && lng != null) {
                  setCoordinatesIfChanged({ lat, lng });
                  saveHomeDeliveryLocation({ lat, lng, fullAddress: healAddress });
                }
              }),
              new Promise((resolve) => setTimeout(resolve, 2500)),
            ]);
          } catch {
            /* continue without coords */
          }
        }
      }

      // Logged-in: only seed from profile default when the buyer has not already
      // chosen a browse delivery location (don't overwrite their selection).
      if (isAuthenticated && !hasUserDeliveryLocation()) {
        try {
          const addrs: any[] = await shippingAddressesAPI.getAll();
          if (cancelled) return;
          const list = Array.isArray(addrs) ? addrs : [];
          const defaultAddr = list.find((a: any) => isDefaultAddress(a));
          if (defaultAddr) {
            const fullAddressStr = (defaultAddr.address || '').trim();
            const city = (defaultAddr.city || '').trim() || parseCityFromAddress(fullAddressStr);
            const stateAbbr = (defaultAddr.state || '').trim() || parseStateFromAddress(fullAddressStr);
            if (city && stateAbbr) {
              const streetLine = (defaultAddr.line1 || '').trim() || parseStreetFromAddress(fullAddressStr);
              const numMatch = streetLine.match(/^(\d+(?:\s*[-]?\s*\d*)?)\s*(.*)$/);
              const streetNumber = numMatch ? numMatch[1].trim() : '';
              const streetName = numMatch ? numMatch[2].trim() : streetLine;
              setUserLocation({ city, state: stateAbbr, streetNumber, streetName, streetAddress: streetLine || undefined });
              const fullAddress = fullAddressStr.length > (city.length + stateAbbr.length + 2)
                ? fullAddressStr
                : [streetLine, city, stateAbbr].filter(Boolean).join(', ');

              const ids = await findLocationIds(city, stateAbbr);
              if (cancelled) return;
              saveHomeDeliveryLocation({
                zip: defaultAddr.postal_code,
                city,
                state: stateAbbr,
                fullAddress,
                label: streetLine || fullAddress,
                city_id: ids.cityId ?? null,
                state_id: ids.stateId ?? null,
              });
              geocodeDeliverTo(fullAddress);
            }
          }
        } catch {
          /* ignore */
        }
      }

      if (!cancelled) setLocationBootstrap('done');
    })();

    const onDeliveryUpdated = () => {
      void applyStoredDeliveryLocation(isCancelled);
    };
    window.addEventListener('delivery-location-updated', onDeliveryUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener('delivery-location-updated', onDeliveryUpdated);
    };
  }, [isAuthenticated, applyStoredDeliveryLocation, setCoordinatesIfChanged]);

  const refreshDefaultAddress = useCallback(() => {
    if (!isAuthenticated) return;
    // Never overwrite a browse location the user already chose.
    if (hasUserDeliveryLocation()) return;
    shippingAddressesAPI.getAll().then((addrs: any[]) => {
      const list = Array.isArray(addrs) ? addrs : [];
      const defaultAddr = list.find((a: any) => isDefaultAddress(a));
      if (!defaultAddr) return;
      const fullAddressStr = (defaultAddr.address || '').trim();
      const city = (defaultAddr.city || '').trim() || parseCityFromAddress(fullAddressStr);
      const stateAbbr = (defaultAddr.state || '').trim() || parseStateFromAddress(fullAddressStr);
      if (!city || !stateAbbr) return;
      const streetLine = (defaultAddr.line1 || '').trim() || parseStreetFromAddress(fullAddressStr);
      const numMatch = streetLine.match(/^(\d+(?:\s*[-]?\s*\d*)?)\s*(.*)$/);
      const streetNumber = numMatch ? numMatch[1].trim() : '';
      const streetName = numMatch ? numMatch[2].trim() : streetLine;
      setUserLocation({ city, state: stateAbbr, streetNumber, streetName, streetAddress: streetLine || undefined });
      const fullAddress = fullAddressStr.length > (city.length + stateAbbr.length + 2)
        ? fullAddressStr
        : [streetLine, city, stateAbbr].filter(Boolean).join(', ');

      findLocationIds(city, stateAbbr).then((ids) => {
        saveHomeDeliveryLocation({
          zip: defaultAddr.postal_code,
          city,
          state: stateAbbr,
          fullAddress,
          label: streetLine || fullAddress,
          city_id: ids.cityId ?? null,
          state_id: ids.stateId ?? null,
        });
        locationsAPI.geocode(fullAddress).then((coords) => {
          const c = coords as { lat: number | null; lng?: number; lon?: number } | null;
          const lat = c?.lat;
          const lng = c && ('lng' in c ? c.lng : c.lon);
          if (lat != null && lng != null) {
            setCoordinatesIfChanged({ lat, lng });
            saveHomeDeliveryLocation({ lat, lng, fullAddress });
          }
        }).catch(() => {});
      }).catch(() => {
        saveHomeDeliveryLocation({
          zip: defaultAddr.postal_code,
          city,
          state: stateAbbr,
          fullAddress,
          label: streetLine || fullAddress,
        });
      });
    }).catch(() => {});
  }, [isAuthenticated, setCoordinatesIfChanged]);

  const detectLocation = async () => {
    setLocationLoading(true);
    try {
      console.log('=== DETECTING LOCATION ===');
      
      // Check if geolocation is available
      if (!navigator.geolocation) {
        toast.error('Location services are not available on this device. Please use the filters to select your location manually.');
        setLocationLoading(false);
        return;
      }
      
      const location = await getCurrentLocation();
      console.log('Location detected:', location);
      setUserLocation({ city: location.city, state: location.state });
      
      // Store user coordinates for distance calculation
      if (location.latitude && location.longitude) {
        setCoordinatesIfChanged({ lat: location.latitude, lng: location.longitude });
      }
      
      if (location.stateAbbreviation) {
        console.log('Looking up location IDs for:', location.city, location.stateAbbreviation);
        const { stateId, cityId } = await findLocationIds(
          location.city || '',
          location.stateAbbreviation
        );
        console.log('Location IDs found:', { stateId, cityId });

        // Save for modal/display; coords drive delivery-range filtering — not city filters.
        saveHomeDeliveryLocation({
          lat: location.latitude,
          lng: location.longitude,
          city: location.city || undefined,
          state: location.stateAbbreviation || location.state || undefined,
          label: [location.city, location.state].filter(Boolean).join(', '),
          city_id: cityId ?? null,
          state_id: stateId ?? null,
        });
      } else {
        console.log('No state abbreviation in location');
        if (location.latitude && location.longitude) {
          saveHomeDeliveryLocation({
            lat: location.latitude,
            lng: location.longitude,
            city: location.city || undefined,
            state: location.state || undefined,
            label: [location.city, location.state].filter(Boolean).join(', '),
          });
        }
      }
    } catch (error: any) {
      console.error('Location detection failed or denied:', error);
      console.error('Error details:', error instanceof Error ? error.message : String(error));
      
      // Provide user-friendly error messages
      if (error.code === 1) {
        toast.error('Location permission denied. Please enable location access in your browser settings or use the filters to select your location manually.');
      } else if (error.code === 2) {
        toast.error('Location unavailable. Please check your device location settings or use the filters to select your location manually.');
      } else if (error.code === 3) {
        toast.error('Location request timed out. Please try again or use the filters to select your location manually.');
      } else {
        toast.error('Unable to detect your location. Please use the filters to select your location manually.');
      }
    } finally {
      setLocationLoading(false);
    }
  };

  // Debounce listings fetch — longer when typing a search query (Algolia).
  // Skip only when filters + coords are unchanged (avoids duplicate loads).
  // Do NOT skip when item_type / category / search / location filters change.
  const lastFetchCoordsRef = useRef<string | null>(null);
  const lastFetchFiltersRef = useRef<string | null>(null);
  useEffect(() => {
    if (locationBootstrap !== 'done') return;
    const coordsKey = homeCoordsKey(userCoordinates);
    const filtersKey = homeFiltersKey(filters);
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      setLoading(false);
      lastFetchCoordsRef.current = coordsKey;
      lastFetchFiltersRef.current = filtersKey;
      return;
    }
    if (
      lastFetchFiltersRef.current === filtersKey &&
      lastFetchCoordsRef.current === coordsKey &&
      listings.length > 0
    ) {
      return;
    }
    const delay = filters.search.trim() ? 300 : 80;
    const t = window.setTimeout(() => {
      lastFetchCoordsRef.current = coordsKey;
      lastFetchFiltersRef.current = filtersKey;
      loadListings(false);
    }, delay);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationBootstrap, filters, userCoordinates]);

  // Don't clear cities when filters change - only clear when state changes
  // This prevents cities from being cleared when other filters change

  const handleSearchSubmit = () => {
    const term = filters.search.trim();
    if (!term) return;
    const params = new URLSearchParams();
    params.set('q', term);
    if (filters.category) params.set('category', filters.category);
    const savedZipCode = loadHomeDeliveryLocation()?.zip;
    if (savedZipCode) params.set('zip', savedZipCode);
    navigate(`/search?${params.toString()}`);
  };

  const loadListings = async (append = false) => {
    const loadId = ++listingsLoadGeneration.current;
    if (append) {
      setLoadingMore(true);
      // Load-more must never trigger the one-time scroll-restore path.
      scrollRestoreDoneRef.current = true;
    } else {
      // Keep showing the current grid while refreshing — only blank the page on first load.
      if (listings.length === 0) setLoading(true);
      setLoadError(null);
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);
      algoliaPageRef.current = 0;
      pendingInRangeRef.current = [];
      pendingDistancesRef.current = {};
    }
    try {
      const searchTerm = filters.search.trim();
      let data: Listing[] = [];
      let apiResponseLength = 0;
      let moreAvailable = false;
      let nextOffset = append ? offsetRef.current : 0;

      if (searchTerm) {
        const locationMap = new Map<number, Listing>();
        if (filters.state_id || filters.city_id) {
          const locParams: Record<string, number | string> = { light: 1, limit: 1000 };
          if (filters.state_id) locParams.state_id = parseInt(filters.state_id, 10);
          if (filters.city_id) locParams.city_id = parseInt(filters.city_id, 10);
          if (filters.item_type) locParams.item_type = filters.item_type;
          if (filters.category) locParams.category = filters.category;
          const locListings = await listingsAPI.getAll(locParams);
          for (const listing of locListings || []) {
            locationMap.set(Number(listing.id), listing);
          }
        }

        const collected: Listing[] = [];
        let page = append ? algoliaPageRef.current : 0;
        let nbPages = 1;
        const maxPage = page + 10;

        while (collected.length < PAGE_SIZE && page < nbPages && page < maxPage) {
          const result = await searchAlgoliaListings({
            query: searchTerm,
            category: filters.category || undefined,
            page,
            hitsPerPage: PAGE_SIZE,
          });
          nbPages = result.nbPages;

          for (const hit of result.hits) {
            const id = hit.id ?? parseInt(hit.objectID, 10);
            if (!id || !matchesItemType(hit.category, filters.item_type)) continue;

            let listing: Listing | undefined;
            if (locationMap.size > 0) {
              listing = locationMap.get(id);
            } else {
              listing = algoliaHitToListing(hit);
            }
            if (!listing) continue;
            collected.push(listing);
            if (collected.length >= PAGE_SIZE) break;
          }

          page += 1;
          if (page >= nbPages) break;
        }

        algoliaPageRef.current = page;
        apiResponseLength = collected.length;
        moreAvailable = page < nbPages;
        data = collected;
      } else {
        const distanceActive = !!userCoordinates;
        let fetchOffset = append ? offsetRef.current : 0;
        const collected: Listing[] = [];
        const pageDistances: Record<number, number> = {};
        // Larger pages when filtering by delivery range so more in-area items surface.
        const fetchLimit = distanceActive ? 100 : PAGE_SIZE;
        // Keep scanning until the grid page is full or the API is exhausted.
        const maxAttempts = distanceActive ? 80 : 1;
        const seenIds = new Set<number>(
          append ? listings.map((l) => Number(l.id)).filter((id) => id > 0) : [],
        );

        // Drain leftovers from the previous page so we don't skip in-range items.
        if (distanceActive) {
          while (collected.length < PAGE_SIZE && pendingInRangeRef.current.length > 0) {
            const next = pendingInRangeRef.current.shift()!;
            const id = Number(next.id);
            if (id > 0 && seenIds.has(id)) continue;
            if (id > 0) seenIds.add(id);
            collected.push(next);
            const d = pendingDistancesRef.current[next.id];
            if (d != null) pageDistances[next.id] = d;
            delete pendingDistancesRef.current[next.id];
          }
        }

        for (
          let attempt = 0;
          attempt < maxAttempts && collected.length < PAGE_SIZE;
          attempt += 1
        ) {
          const params: any = { limit: fetchLimit, offset: fetchOffset, light: 1 };
          if (!distanceActive && filters.state_id) params.state_id = parseInt(filters.state_id);
          if (!distanceActive && filters.city_id) params.city_id = parseInt(filters.city_id);
          if (filters.category) params.category = filters.category;
          if (filters.item_type) params.item_type = filters.item_type;

          const page = await listingsAPI.getAll(params);
          apiResponseLength = Array.isArray(page) ? page.length : 0;
          moreAvailable = apiResponseLength >= fetchLimit;
          fetchOffset += apiResponseLength;

          let pageData = page || [];
          if (distanceActive && pageData.length > 0) {
            const filtered = await filterListingsByDeliveryRange(
              pageData,
              userCoordinates,
              { keepUserId: user?.id ?? null },
            );
            pageData = filtered.listings;
            Object.assign(pageDistances, filtered.distances);
            Object.assign(pendingDistancesRef.current, filtered.distances);
          }

          if (loadId !== listingsLoadGeneration.current) return;

          for (const listing of pageData) {
            const id = Number(listing.id);
            if (id > 0 && seenIds.has(id)) continue;
            if (id > 0) seenIds.add(id);
            if (collected.length < PAGE_SIZE) {
              collected.push(listing);
              delete pendingDistancesRef.current[listing.id];
            } else if (distanceActive) {
              pendingInRangeRef.current.push(listing);
            }
          }

          if (collected.length >= PAGE_SIZE || !moreAvailable) break;
          if (!distanceActive) break;
        }

        data = collected;
        nextOffset = fetchOffset;
        if (distanceActive) {
          moreAvailable = moreAvailable || pendingInRangeRef.current.length > 0;
          setListingDistances((prev) => (append ? { ...prev, ...pageDistances } : pageDistances));
        } else if (!append) {
          setListingDistances({});
        }
      }

      if (loadId !== listingsLoadGeneration.current) return;

      const displayCount = data?.length ?? 0;
      setHasMore(moreAvailable);
      if (searchTerm) {
        const next = append ? offsetRef.current + displayCount : displayCount;
        offsetRef.current = next;
        setOffset(next);
      } else {
        offsetRef.current = nextOffset;
        setOffset(nextOffset);
      }

      // Append only — do not touch window.scroll*. Native scroll anchoring is also
      // disabled on the grid so the viewport stays put while cards are added below.
      if (append) {
        if ((data || []).length === 0) {
          setHasMore(moreAvailable);
        } else {
          setListings((prev) => {
            const existing = new Set(prev.map((l) => Number(l.id)));
            const fresh = (data || []).filter((l) => !existing.has(Number(l.id)));
            return fresh.length ? [...prev, ...fresh] : prev;
          });
        }
      } else {
        setListings(data || []);
      }
    } catch (error) {
      console.error('Error loading listings:', error);
      if (error instanceof Error && 'response' in error) {
        const axiosError = error as any;
        console.error('Response status:', axiosError.response?.status);
        console.error('Response data:', axiosError.response?.data);
      }
      if (!append) setLoadError(formatListingsLoadError(error));
    } finally {
      if (loadId === listingsLoadGeneration.current) {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    }
  };

  const handleLoadMore = () => {
    if (loadingMore) return;
    scrollRestoreDoneRef.current = true;
    void loadListings(true);
  };

  useEffect(() => {
    return () => {
      saveHomeBrowseState(window.scrollY, listings.length);
      if (listings.length > 0) {
        saveHomeListingsCache({
          listings,
          listingDistances,
          offset,
          hasMore: hasMore || pendingInRangeRef.current.length > 0,
          filtersKey: homeFiltersKey(filters),
          coordsKey: homeCoordsKey(userCoordinates),
          scrollY: window.scrollY,
          pendingInRange: pendingInRangeRef.current.slice(),
          pendingDistances: { ...pendingDistancesRef.current },
        });
      }
    };
  }, [listings, listingDistances, offset, hasMore, filters, userCoordinates]);

  useEffect(() => {
    // One-time scroll restore when returning to Home — never on Load more.
    if (scrollRestoreDoneRef.current) return;
    if (locationBootstrap !== 'done' || loading) return;

    const cachedScroll = initialCache?.scrollY;
    const saved = restoreBrowseRef.current;
    const targetScroll = saved?.scrollY ?? cachedScroll ?? 0;

    if (saved) {
      const needsMoreListings = listings.length < saved.listingsCount && hasMore;
      if (needsMoreListings) {
        if (!loadingMore) void loadListings(true);
        return;
      }
      restoreBrowseRef.current = null;
    }

    scrollRestoreDoneRef.current = true;
    if (targetScroll > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: targetScroll, left: 0, behavior: 'auto' });
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings.length, loading, loadingMore, hasMore, locationBootstrap]);

  const handleZipCodeLookup = async () => {
    if (!zipCode.trim() || !/^\d{5}$/.test(zipCode.trim())) {
      setZipCodeError('Please enter a valid 5-digit zip code');
      return;
    }

    setZipCodeLoading(true);
    setZipCodeError('');

    try {
      const locationData = await locationsAPI.lookupZipCode(zipCode.trim());
      
        if (locationData.state_id) {
          // Set user location for display — do not apply city/state as listing filters
          // (that hides in-range listings from nearby cities).
          setUserLocation({ city: locationData.city_name, state: locationData.state });
          
          // Get coordinates for distance calculation
          let lat = locationData.latitude ?? null;
          let lng = locationData.longitude ?? null;
          if (lat != null && lng != null) {
            setCoordinatesIfChanged({ lat, lng });
          } else if (locationData.city_name && locationData.state) {
            const coords = await geocodeLocation(locationData.city_name, locationData.state);
            if (coords) {
              lat = coords.lat;
              lng = coords.lng;
              setCoordinatesIfChanged({ lat: coords.lat, lng: coords.lng });
            }
          }
          
          saveHomeDeliveryLocation({
            zip: zipCode.trim(),
            city: locationData.city_name,
            state: locationData.state_abbreviation || locationData.state,
            lat,
            lng,
            label: [locationData.city_name, locationData.state].filter(Boolean).join(', '),
            city_id: locationData.city_id ?? null,
            state_id: locationData.state_id ?? null,
          });
          
          setZipCodeError('');
        } else {
          setZipCodeError('Location not found for this zip code');
        }
    } catch (error: any) {
      console.error('Error looking up zip code:', error);
      setZipCodeError(error.response?.data?.error || 'Failed to lookup zip code');
    } finally {
      setZipCodeLoading(false);
    }
  };

  const handleStateChange = async (stateId: string) => {
    console.log('=== HANDLE STATE CHANGE CALLED ===', stateId);
    setFilters(prev => {
      console.log('Setting filters, prev state_id:', prev.state_id, 'new state_id:', stateId);
      return { ...prev, state_id: stateId, city_id: '' };
    });
    if (stateId) {
      setCities([]); // Clear cities immediately
      console.log('Cleared cities, now fetching for state:', stateId);

      try {
        console.log('=== FETCHING CITIES VIA API ===');
        console.log('State ID:', stateId);
        const startTime = Date.now();
        const citiesData = await locationsAPI.getCitiesByState(parseInt(stateId));
        const endTime = Date.now();
        console.log('API call took:', endTime - startTime, 'ms');
        console.log('Raw response:', citiesData);
        console.log('Is array:', Array.isArray(citiesData));
        console.log('Cities count:', citiesData?.length || 0);
        
        if (Array.isArray(citiesData) && citiesData.length > 0) {
          console.log('First city:', citiesData[0]);
          console.log('Setting cities state with', citiesData.length, 'cities');
          setCities(citiesData);
        } else {
          console.warn('Cities data is not a valid array:', citiesData);
          setCities([]);
        }
      } catch (error: any) {
        console.error('=== ERROR LOADING CITIES ===');
        console.error('Error:', error);
        console.error('Error message:', error?.message);
        if (error?.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        if (error?.config) {
          console.error('Request URL:', error.config.url);
          console.error('Request baseURL:', error.config.baseURL);
        }
        setCities([]);
      }
    } else {
      setCities([]);
    }
  };

  const categories = LISTING_CATEGORY_KEYS;

  const foodCategories = ['food', 'shop']; // Categories that are food-related

  const activeFilterCount = [filters.state_id, filters.city_id, filters.category]
    .filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-x-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 min-w-0">
        {/* Item Type Selector */}
        <div className="mb-6 sm:mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6 overflow-hidden">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-5 min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 shrink-0">What are you looking for?</h2>

            {!userLocation ? (
              <button
                type="button"
                onClick={() => setShowLocationModal(true)}
                className="flex w-full sm:w-auto items-center gap-3 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all text-sm font-semibold text-gray-900"
              >
                <i className="ri-map-pin-2-fill text-green-600 text-xl shrink-0 leading-none" aria-hidden />
                <span className="truncate">Where to deliver?</span>
              </button>
            ) : (
              <div className="flex w-full sm:w-auto sm:min-w-[280px] items-center gap-3 min-w-0">
                <div className="flex flex-1 min-w-0 items-center gap-3 px-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200">
                  <i className="ri-map-pin-2-fill text-green-600 text-xl shrink-0 leading-none" aria-hidden />
                  <span className="min-w-0 flex-1 text-sm font-semibold text-gray-900 truncate">
                    {(() => {
                      const streetAddress = userLocation.streetAddress?.trim()
                        || [userLocation.streetNumber, userLocation.streetName].filter(Boolean).join(' ').trim();
                      return streetAddress || 'Location set';
                    })()}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowLocationModal(true)}
                  className="shrink-0 px-1 text-sm text-green-600 hover:text-green-700 font-semibold"
                >
                  Edit
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-3 sm:gap-4 min-w-0">
            <button
              type="button"
              onClick={() => setFilters({ ...filters, item_type: '', category: '' })}
              className={`flex w-full min-w-0 box-border flex-row items-center justify-center gap-3 px-4 py-3.5 sm:py-4 rounded-lg border transition-all font-semibold text-base ${
                filters.item_type === ''
                  ? 'border-green-500 bg-green-50 text-green-700 sm:border-2'
                  : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50 sm:border-2 sm:border-gray-300'
              }`}
            >
              <i className="ri-store-3-line text-2xl" aria-hidden />
              <span>Everything</span>
            </button>
            <button
              type="button"
              onClick={() => setFilters({ ...filters, item_type: 'other', category: '' })}
              className={`flex w-full min-w-0 box-border flex-row items-center justify-center gap-3 px-4 py-3.5 sm:py-4 rounded-lg border transition-all font-semibold text-base ${
                filters.item_type === 'other'
                  ? 'border-green-500 bg-green-50 text-green-700 sm:border-2'
                  : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50 sm:border-2 sm:border-gray-300'
              }`}
            >
              <i className="ri-shopping-bag-3-line text-2xl" aria-hidden />
              <span>Items for Sale</span>
            </button>
            <button
              type="button"
              onClick={() => setFilters({ ...filters, item_type: 'food', category: '' })}
              className={`flex w-full min-w-0 box-border flex-row items-center justify-center gap-3 px-4 py-3.5 sm:py-4 rounded-lg border transition-all font-semibold text-base ${
                filters.item_type === 'food'
                  ? 'border-green-500 bg-green-50 text-green-700 sm:border-orange-500 sm:bg-orange-50 sm:text-orange-700 sm:border-2'
                  : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50 sm:border-2 sm:border-gray-300'
              }`}
            >
              <i className="ri-restaurant-line text-2xl" aria-hidden />
              <span className="sm:hidden">Food</span>
              <span className="hidden sm:inline">Homemade Meals & Food</span>
            </button>
          </div>
        </div>

        <div className="mb-6 sm:mb-8 flex items-center justify-between">
          <div>
            <p className="text-gray-600 text-sm sm:text-base">
              {selectedCity && selectedState
                ? `Showing items near ${selectedCity.name}, ${selectedState.abbreviation}`
                : selectedState
                ? `Showing items in ${selectedState.name}`
                : userLocation 
                ? `Showing items near ${userLocation.city || userLocation.state || 'your location'}`
                : 'Browse local listings'
              }
            </p>
          </div>
        </div>

        {/* Filters — mobile only: compact search bar with a filter toggle (like the app) */}
        <div className="sm:hidden bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6 overflow-hidden">
          <div className="flex items-center gap-2 min-w-0 max-w-full">
            <div className="relative flex-1 basis-0 min-w-0">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none" aria-hidden />
              <input
                type="search"
                enterKeyHint="search"
                placeholder="Search..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchSubmit();
                  }
                }}
                className="w-full max-w-full min-w-0 box-border bg-white pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-0 text-base"
              />
            </div>
            <button
              type="button"
              onClick={() => setMobileFiltersOpen((open) => !open)}
              aria-expanded={mobileFiltersOpen}
              aria-label="Filters"
              className={`relative shrink-0 flex items-center justify-center w-11 h-11 rounded-lg border transition-all ${
                mobileFiltersOpen || activeFilterCount > 0
                  ? 'bg-green-600 border-green-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className="ri-equalizer-line text-xl" aria-hidden />
              {activeFilterCount > 0 && (
                <span className="absolute top-0 right-0 translate-x-1/4 -translate-y-1/4 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-green-700 bg-white border border-green-600 rounded-full pointer-events-none">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
          {mobileFiltersOpen && (
            <div className="flex flex-col gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <select
                  value={filters.state_id}
                  onChange={(e) => handleStateChange(e.target.value)}
                  className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base transition-all"
                >
                  <option value="">All States</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>{state.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <select
                  value={filters.city_id}
                  onChange={(e) => setFilters(prev => ({ ...prev, city_id: e.target.value }))}
                  disabled={!filters.state_id}
                  className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-base transition-all"
                >
                  <option value="">All Cities</option>
                  {cities.length > 0 ? (
                    cities.map((city) => (
                      <option key={city.id} value={city.id.toString()}>{city.name}</option>
                    ))
                  ) : (
                    filters.state_id && <option value="" disabled>Loading cities...</option>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base transition-all"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{formatListingCategoryLabel(cat)}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFilters({ state_id: '', city_id: '', category: '', search: '', item_type: '' });
                }}
                className="w-full px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium border border-gray-300 hover:border-gray-400 transition-all shadow-sm"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Filters — desktop (original layout) */}
        <div className="hidden sm:block bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Filters</h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-2 mb-5 sm:mb-6">
            <span className="font-medium text-gray-700">Location</span> (state/city) narrows the area you browse.
            With a <span className="font-medium text-gray-700">delivery address</span> set above, we show listings with{' '}
            <span className="font-medium text-gray-700">delivery available</span> to you when possible.
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-4">
            <div className="w-full sm:flex-1 sm:min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <input
                type="text"
                placeholder="Search items..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchSubmit();
                  }
                }}
                className="w-full bg-white px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm transition-all shadow-sm hover:shadow-md"
              />
            </div>
            <div className="w-full sm:flex-1 sm:min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State
              </label>
              <select
                value={filters.state_id}
                onChange={(e) => handleStateChange(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm transition-all shadow-sm hover:shadow-md"
              >
                <option value="">All States</option>
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:flex-1 sm:min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <select
                value={filters.city_id}
                onChange={(e) => setFilters(prev => ({ ...prev, city_id: e.target.value }))}
                disabled={!filters.state_id}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm transition-all shadow-sm hover:shadow-md"
              >
                <option value="">All Cities</option>
                {cities.length > 0 ? (
                  cities.map((city) => (
                    <option key={city.id} value={city.id.toString()}>
                      {city.name}
                    </option>
                  ))
                ) : (
                  filters.state_id && <option value="" disabled>Loading cities...</option>
                )}
              </select>
            </div>
            <div className="w-full sm:flex-1 sm:min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm transition-all shadow-sm hover:shadow-md"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {formatListingCategoryLabel(cat)}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-auto">
              <button
                type="button"
                onClick={() => {
                  setFilters({ state_id: '', city_id: '', category: '', search: '', item_type: '' });
                }}
                className="w-full sm:w-auto min-w-[120px] px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium border border-gray-300 hover:border-gray-400 transition-all shadow-sm"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="text-center py-16 sm:py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-green-500"></div>
            <p className="mt-4 text-gray-600 text-base sm:text-lg">Loading listings...</p>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16 sm:py-20 bg-white rounded-xl shadow-sm border border-gray-200">
            {loadError ? (
              <>
                <i className="ri-error-warning-line text-6xl text-amber-500 mb-4"></i>
                <h3 className="text-lg sm:text-xl text-gray-900 font-semibold mb-2">Couldn&apos;t load listings</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6 max-w-md mx-auto">{loadError}</p>
                <button
                  type="button"
                  onClick={() => loadListings()}
                  className="px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium border-0 transition-all shadow-sm"
                >
                  Retry
                </button>
              </>
            ) : (
              <>
                <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-lg sm:text-xl text-gray-900 font-semibold mb-2">No listings found</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6">
                  {filters.state_id || filters.city_id || filters.category || filters.search || filters.item_type
                    ? 'Try adjusting your filters or search terms'
                    : 'Be the first to create one!'}
                </p>
                {(filters.state_id || filters.city_id || filters.category || filters.search || filters.item_type) && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilters({ state_id: '', city_id: '', category: '', search: '', item_type: '' });
                    }}
                    className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium border border-gray-300 hover:border-gray-400 transition-all shadow-sm"
                  >
                    Clear All Filters
                  </button>
                )}
                {isAuthenticated && !filters.state_id && !filters.city_id && !filters.category && !filters.search && (
                  <Link
                    to="/sell"
                    className="inline-block px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-all shadow-md hover:shadow-lg"
                  >
                    Sell in the app
                  </Link>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div
              id="home-listings-grid"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5 [overflow-anchor:none]"
            >
              {listings.map((listing, index) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  showOpenStatus={false}
                  showBadges={false}
                  distanceMiles={listingDistances[listing.id]}
                  priority={index < 8}
                />
              ))}
            </div>
            {hasMore && (
              <div
                id="home-load-more"
                className="mt-8 flex justify-center [overflow-anchor:none]"
              >
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  aria-busy={loadingMore}
                  className="inline-flex h-12 min-w-[9.5rem] items-center justify-center gap-2 px-6 bg-green-500 hover:bg-green-600 disabled:opacity-70 text-white font-medium rounded-lg border-0 transition-colors"
                >
                  {loadingMore ? (
                    <>
                      <span
                        className="inline-block h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent"
                        aria-hidden
                      />
                      <span>Loading</span>
                    </>
                  ) : (
                    'Load more'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <HomeLocationModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onAddressesUpdated={refreshDefaultAddress}
        onLocationSelect={async (cityId, stateId, cityName, stateName, fullAddress?: string) => {
          // Delivery address sets coords for range filtering — not city/state listing filters.
          const streetLine = fullAddress ? fullAddress.split(',')[0]?.trim() || '' : '';
          const numMatch = streetLine.match(/^(\d+(?:\s*[-]?\s*\d*)?)\s*(.*)$/);
          const streetNumber = numMatch ? numMatch[1].trim() : '';
          const streetName = numMatch ? numMatch[2].trim() : streetLine;
          setUserLocation({ city: cityName, state: stateName, streetAddress: streetLine, streetNumber, streetName });
          const stored = await saveDeliveryLocationFromModal(cityName, stateName, fullAddress, cityId, stateId);
          if (stored?.lat != null && stored?.lng != null) {
            setCoordinatesIfChanged({ lat: stored.lat, lng: stored.lng });
          }
          setShowLocationModal(false);
        }}
        currentCityId={
          filters.city_id
            ? parseInt(filters.city_id, 10)
            : loadHomeDeliveryLocation()?.city_id
        }
        currentStateId={
          filters.state_id
            ? parseInt(filters.state_id, 10)
            : loadHomeDeliveryLocation()?.state_id
        }
      />
    </div>
  );
};

export default Home;

